import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, CardBody } from "reactstrap";
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import "../../../assets/custom css/loadoverlay.scss";

import GenFilter from "../../../components/Table Filters/GenFilter";
import DataTable from 'react-data-table-component';

import Alerts from "../../../components/Alerts/alerts";

import ComplaintCategoriesService from "../../Complaints/ComplaintCategories/complaintCategories.service";
import AuthService from "../../Authentication/auth.service";

const ComplaintCategoriesTab = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  function fetchCategories() {
    setLoading(true);
    ComplaintCategoriesService.getAll({}).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      }
      else {
        setCategoriesList(response.data.data);
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent)
        }
        else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."))
        }
      })
  };

  const TBcolumns = [
    {
      name: t('Category Name'),
      selector: row => row.name,
    },
    {
      name: t('Status'),
      selector: (row) => {
        if (parseInt(row.status) === 1) {
          return (<div> <button className="btn btn-success btn">{t('Active')}</button> </div>);
        } else {
          return (<div> <button className="btn btn-danger btn">{t('Inactive')}</button> </div>);
        }
      }
    },
    {
      name: t('Actions'),
      selector: (row) => {
        return (
          <div>
            <Tooltip placement="top" title="Edit" arrow>
              <Link className="EditData" to="/update-complaint-category" state={row} >
                <i className="pe-7s-pen icon-gradient bg-arielle-smile btn-lg px-1 "> </i>
              </Link>
            </Tooltip >
            {parseInt(row.status) === 1 &&
              <Tooltip placement="top" title="Change Status" arrow>
                <a onClick={() => changeStatus(row)} style={{ cursor: "pointer" }} className="text-info btn-lg px-1">
                  <FontAwesomeIcon icon={["fas", "lock-open"]} style={{ marginBottom: "2px" }} />
                </a>
              </Tooltip >
            }
            {parseInt(row.status) === 0 &&
              <Tooltip placement="top" title="Change Status" arrow>
                <a onClick={() => changeStatus(row)} style={{ cursor: "pointer" }} className="text-danger btn-lg px-1">
                  <FontAwesomeIcon icon={["fas", "lock"]} style={{ marginBottom: "2px" }} />
                </a>
              </Tooltip >
            }
            <Tooltip placement="top" title="Delete" arrow>
              <a className="DeleteData" onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-trash icon-gradient bg-ripe-malin btn-lg px-0"> </i>
              </a>
            </Tooltip >
          </div>
        );
      }
    },
  ];

  const [filterText, setFilterText] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const filteredItems = (categoriesList && categoriesList.filter(
    item => item.name && item.name.toLowerCase().includes(filterText.toLowerCase()),
  ));
  const handleSearch = (searchText) => {
    setFilterText(searchText);
  };

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText('');
    }
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <GenFilter
        onSearch={handleSearch}
        onClear={handleClear}
        filterText={filterText}
      />
    );
  }, [filterText, resetPaginationToggle])

  function changeStatus(category) {
    let msg = t("Are you sure you want to change the status of this category?")
    Alerts.swalConfirmGeneralAlert(msg).then(res => {
      if (res === true) {
        sendToggleStatus(category._id)
      }
    })
  };

  function sendToggleStatus(categoryId) {
    setLoading(true);
    ComplaintCategoriesService.toggleStatus(categoryId).then((response) => {
      setLoading(false);
      fetchCategories();
      if (response.data.status) {
        if (response.data.message !== '') {
          Alerts.swalSuccessAlert(response.data.message)
        }
        else {
          Alerts.swalSuccessAlert(t("Status Updated Successfully."));
        }
      }
      else {
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't update status at the moment."))
        }
      }
    },
      (error) => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Operation failed."))
      });
  }

  function confirmDelete(_id) {
    let msg = t("Once deleted, you will not be able to recover this category!")
    Alerts.swalConfirmDeleteAlert(msg).then(res => {
      if (res === true) {
        sendDelete(_id)
      }
    })
  };

  function sendDelete(id) {
    setLoading(true);
    ComplaintCategoriesService.deleteCategory(id).then((response) => {
      setLoading(false);
      fetchCategories();
      if (response.data.status) {
        if (response.data.message !== '') {
          Alerts.swalSuccessAlert(response.data.message)
        }
        else {
          Alerts.swalSuccessAlert(t("Category Deleted Successfully."))
        }
      }
      else {
        if (response.data.message !== '') {
          Alerts.swalErrorAlert(response.data.message)
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Category at the moment."))
        }
      }
    },
      (error) => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Delete operation failed"))
      })
  };

  return (
    <Row>
      <Col md="12">
        <Card className="main-card mb-3">
          <CardBody>
            <LoadingOverlay tag="div" active={loading}
              styles={{ overlay: (base) => ({ ...base }) }}
              spinner={<DNALoader />}>
              <Col md="6" style={{ float: "left", position: "absolute", zIndex: "1" }}>
                {subHeaderComponentMemo}
              </Col>
              <DataTable
                columns={TBcolumns}
                data={filteredItems}
                pagination
                paginationResetDefaultPage={resetPaginationToggle}
                subHeader
                subHeaderComponent={<Link to="/add-complaint-category" className="AddData">
                  <button type="button" className="btn btn-custom-color btn-hover-shine me-3 btn">{t('Add New')}</button>
                </Link>}
                selectableRowDisabled={true}
                persistTableHead
              />
            </LoadingOverlay>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ComplaintCategoriesTab;

